import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import { useFormik } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLayerGroup, faCopy, faTrash } from "@fortawesome/free-solid-svg-icons";
import crudApi from "@serie3/common/api/crudApi";
import listHandler from "@serie3/common/Domain/listHandler";
import MediaplannerGroupEndpoints from "@serie3/mymediaplanner/Modules/Groups/endpoints";
import ContentPillar from "@serie3/mymediaplanner/Modules/Social/Entity/ContentPillar";
import ContentPillarForm from "@serie3/mymediaplanner/Modules/Social/Forms/ContentPillarForm";
import NewModal from "../../../../shared/Modals/NewModal";
import ContentPillarFormView from "../../ContentPillars/Subcomponents/ContentPillarFormView";

const PILLAR_COLORS = ["#038edc", "#51d28c", "#f1734f", "#564ab1", "#f7cc53", "#5fd0f3"];

const GroupPillars = ({ group, rights }) => {
  const { t } = useTranslation();
  const [pillars, setPillars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editPillar, setEditPillar] = useState(null);

  const isAdmin = rights?.admin === true;

  const onSubmit = async (values) => {
    if (values.id > 0) {
      // edit — reuse standard edit endpoint
      const response = await crudApi.edit(
        "social/contentpillar/edit",
        JSON.stringify(values, null, 2)
      );
      listHandler.updateList(response, JSON.stringify(values, null, 2), pillars, setPillars);
    } else {
      const response = await crudApi.createNew(
        MediaplannerGroupEndpoints.endpoints.pillars.new + group.id,
        JSON.stringify(values, null, 2)
      );
      listHandler.newToList(response, pillars, setPillars, null, true);
    }
    setShowForm(false);
  };

  const formik = useFormik({
    initialValues: ContentPillar,
    onSubmit,
  });

  useEffect(() => {
    if (!group?.id) return;
    const fetch = async () => {
      setLoading(true);
      const response = await crudApi.justGet(
        MediaplannerGroupEndpoints.endpoints.pillars.list + group.id
      );
      if (response.ok) setPillars(response.data.contentPillars ?? []);
      setLoading(false);
    };
    fetch();
  }, [group?.id]);

  const deletePillar = async (pillar, index) => {
    const response = await crudApi.del(
      MediaplannerGroupEndpoints.endpoints.pillars.delete + group.id + "/",
      pillar.id
    );
    if (response.status === 200) {
      listHandler.deleteFromList(index, Promise.resolve(response), setPillars, pillars);
    }
  };

  const copyToPersonal = async (pillar) => {
    await crudApi.justGet(
      MediaplannerGroupEndpoints.endpoints.pillars.copy + group.id + "/" + pillar.id
    );
  };

  const openCreate = () => {
    setEditPillar(null);
    setShowForm(true);
  };

  const openEdit = (pillar) => {
    setEditPillar(pillar);
    setShowForm(true);
  };

  return (
    <>
      <NewModal
        title={editPillar ? t("Backend.Post.ContentPillars.Modal.Title") : t("Backend.Post.ContentPillars.New")}
        show={showForm}
        setShow={setShowForm}
        onShow={() => {
          editPillar
            ? formik.setValues(ContentPillarForm.setEditData(editPillar))
            : formik.setValues(ContentPillar);
        }}
        onExit={() => formik.resetForm()}
        ExtraButton={() => (
          <Button onClick={() => formik.submitForm()}>{t("Backend.Buttons.Save")}</Button>
        )}
      >
        <ContentPillarFormView formik={formik} />
      </NewModal>

      <div className="d-flex align-items-center justify-content-between mb-4">
        <h5 style={{ fontWeight: 700, color: "#1a2236", margin: 0, display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ width: 32, height: 32, borderRadius: 8, background: "rgba(86,74,177,0.12)", display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
            <FontAwesomeIcon icon={faLayerGroup} style={{ color: "#564ab1", fontSize: 14 }} />
          </span>
          {t("Backend.Post.ContentPillars.Title")}
        </h5>
        <button
          type="button"
          onClick={openCreate}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
            background: "#51d28c", color: "#fff",
            fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 14,
          }}
        >
          <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: 11 }} />
          {t("Backend.Post.ContentPillars.New")}
        </button>
      </div>

      {loading ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {[1, 2, 3].map((i) => (
            <div key={i} className="skeleton" style={{ height: 72, borderRadius: 12 }} />
          ))}
        </div>
      ) : pillars.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: "#9299a8" }}>
          <FontAwesomeIcon icon={faLayerGroup} style={{ fontSize: 32, marginBottom: 12, display: "block" }} />
          <p style={{ fontSize: 14 }}>{t("Backend.Post.ContentPillars.Empty", "No content pillars yet.")}</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {pillars.map((pillar, index) => {
            const color = PILLAR_COLORS[index % PILLAR_COLORS.length];
            return (
              <div
                key={pillar.id}
                style={{
                  display: "flex", alignItems: "center", gap: 14,
                  padding: "14px 18px", borderRadius: 12,
                  background: "#fff", border: "1px solid #eaecf0",
                  borderLeft: `4px solid ${color}`,
                  boxShadow: "0 2px 6px rgba(0,0,0,0.04)",
                }}
              >
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
                <div
                  style={{ flex: 1, minWidth: 0, cursor: "pointer" }}
                  onClick={() => openEdit(pillar)}
                >
                  <p style={{ margin: 0, fontWeight: 700, fontSize: 14, color: "#1a2236" }}>{pillar.pillar}</p>
                  {pillar.description && (
                    <p style={{ margin: 0, fontSize: 12, color: "#9299a8", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {pillar.description}
                    </p>
                  )}
                </div>

                {/* Copy to personal */}
                <button
                  type="button"
                  title={t("Backend.Post.ContentPillars.CopyToPersonal", "Copy to my pillars")}
                  onClick={() => copyToPersonal(pillar)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#038edc", padding: "4px 8px" }}
                >
                  <FontAwesomeIcon icon={faCopy} style={{ fontSize: 14 }} />
                </button>

                {/* Delete — admin only */}
                {isAdmin && (
                  <button
                    type="button"
                    title={t("Backend.Files.Buttons.DeleteFile")}
                    onClick={() => deletePillar(pillar, index)}
                    style={{ background: "none", border: "none", cursor: "pointer", color: "#f34e4e", padding: "4px 8px" }}
                  >
                    <FontAwesomeIcon icon={faTrash} style={{ fontSize: 14 }} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </>
  );
};

export default GroupPillars;
